import {
  Dialog,
  DialogTitle,
  FormHelperText,
  Box,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  InputAdornment,
  IconButton
} from '@mui/material'
import { useState, useRef, useEffect } from 'react'
import { addObject } from '../../../api/apiClientCrud'
import { toast } from 'react-toastify'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

function ForgotPassword(props) {
  const { setOpen, open, ...other } = props
  const [errors, setErrors] = useState({})
  const [data, setData] = useState({ email: '', otp: '', password: '', repassword: '' })
  const [isNextStep, setNextStep] = useState(false)
  const [isFailed, setIsFailed] = useState(false)
  const [loading, setLoading] = useState(false)
  const toastId = useRef(null)

  const otpRef = useRef(null)

  useEffect(() => {
    if (isNextStep && otpRef.current) {
      otpRef.current.focus()
    }
  }, [isNextStep])

  const handleFocus = (e) => {
    const { name } = e.target
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }))
    }
    if (isFailed) setIsFailed(false)
  }

  const [showPassword, setShowPassword] = useState(false)
  const [showRepassword, setShowRepassword] = useState(false)
  const togglePasswordVisibility = () => setShowPassword((show) => !show)
  const toggleRepasswordVisibility = () => setShowRepassword((show) => !show)

  const validateInputs = () => {
    let tempErrors = {}
    let valid = true
    setIsFailed(false)

    if (!isNextStep) {
      if (!data.email || data.email.trim() === '') {
        tempErrors.email = 'Saisir une adresse email'
        valid = false
      } else if (
        !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
          data.email
        )
      ) {
        tempErrors.email = 'Email invalide'
        valid = false
      }
    } else {
      if (!data.otp || data.otp.trim() === '') {
        tempErrors.otp = 'OTP obligatoire'
        valid = false
      } else if (!/^\d{4}$/.test(data.otp)) {
        tempErrors.otp = 'OTP doit contenir 4 chiffres'
        valid = false
      }
      if (!data.password) {
        tempErrors.password = 'Mot de passe obligatoire'
        valid = false
      } else if (data.password.length < 8) {
        tempErrors.password = 'Mot de passe doit contenir au moins 8 caractères'
        valid = false
      }
      if (!data.repassword) {
        tempErrors.repassword = 'Confirmer le mot de passe'
        valid = false
      } else if (data.password !== data.repassword) {
        tempErrors.repassword = 'Mots de passe différents'
        valid = false
      }
    }

    setErrors(tempErrors)
    return valid
  }

  const handleNext = async () => {
    if (loading) return
    setLoading(true)

    if (!validateInputs()) {
      setLoading(false)
      return
    }

    try {
      if (!isNextStep) {
        toastId.current = toast.loading('Demande en cours...')
        await addObject('forgotPassword', { email: data.email })
        toast.update(toastId.current, {
          type: 'success',
          render: 'Email envoyé, vérifiez votre boîte email',
          autoClose: 2000,
          isLoading: false
        })
        setNextStep(true)
        setErrors({})
      } else {
        toastId.current = toast.loading('Demande en cours...')
        await addObject('resetPassword', {
          email: data.email,
          otp: data.otp,
          password: data.password
        })
        toast.update(toastId.current, {
          type: 'success',
          render: 'Mot de passe mis à jour',
          autoClose: 2000,
          isLoading: false
        })
        setOpen(false)
        setData({ email: '', otp: '', password: '', repassword: '' })
        setNextStep(false)
        setErrors({})
      }
    } catch {
      setIsFailed(true)
      toast.update(toastId.current, {
        type: 'error',
        render: 'Erreur, vérifiez vos informations',
        autoClose: 2000,
        isLoading: false
      })
    }
    setLoading(false)
  }

  const handleCancel = () => {
    setData({ email: '', otp: '', password: '', repassword: '' })
    setOpen(false)
    setNextStep(false)
    setErrors({})
    setIsFailed(false)
    setLoading(false)
  }

  const handleInput = (e) => {
    setData({ ...data, [e.target.name]: e.target.value })
    if (errors[e.target.name]) {
      setErrors((prev) => ({ ...prev, [e.target.name]: undefined }))
    }
    if (isFailed) setIsFailed(false)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    handleNext()
  }

  return (
    <Dialog
      maxWidth='lg'
      fullWidth
      open={open}
      {...other}
      slotProps={{
        paper: {
          sx: {
            width: '500px',
            maxWidth: '90vw'
          }
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>Mot de passe oublié</Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box component='form' onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            margin='normal'
            required
            id='email'
            label='Email'
            name='email'
            autoComplete='email'
            autoFocus
            disabled={isNextStep || loading}
            error={Boolean(errors.email)}
            helperText={errors.email ?? ''}
            onChange={handleInput}
            onFocus={handleFocus}
          />
          {isNextStep && (
            <>
              <TextField
                margin='normal'
                required
                id='otp'
                label='OTP'
                name='otp'
                inputRef={otpRef}
                disabled={loading}
                error={Boolean(errors.otp)}
                helperText={errors.otp ?? ''}
                onChange={handleInput}
                onFocus={handleFocus}
              />
              <TextField
                margin='normal'
                required
                id='password'
                label='Mot de passe'
                name='password'
                type={showPassword ? 'text' : 'password'}
                disabled={loading}
                error={Boolean(errors.password)}
                helperText={errors.password ?? ''}
                onChange={handleInput}
                onFocus={handleFocus}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge='end'
                        aria-label={showPassword ? 'Cacher le mot de passe' : 'Afficher le mot de passe'}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <TextField
                margin='normal'
                required
                id='repassword'
                label='Confirmation Mot de passe'
                name='repassword'
                type={showRepassword ? 'text' : 'password'}
                disabled={loading}
                error={Boolean(errors.repassword)}
                helperText={errors.repassword ?? ''}
                onChange={handleInput}
                onFocus={handleFocus}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={toggleRepasswordVisibility}
                        edge='end'
                        aria-label={showRepassword ? 'Cacher le mot de passe' : 'Afficher le mot de passe'}
                      >
                        {showRepassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              {isFailed && <FormHelperText error>Erreur, veuillez vérifier vos informations</FormHelperText>}
            </>
          )}
          <DialogActions sx={{ mt: 2 }}>
            <Button onClick={handleCancel} disabled={loading}>
              Annuler
            </Button>
            <Button type='submit' disabled={loading}>
              {loading ? (isNextStep ? 'Envoi en cours...' : 'Demande en cours...') : 'Envoyer'}
            </Button>
          </DialogActions>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default ForgotPassword
